/**
 * Order Table Configuration
 * Defines all column configurations and styling rules for the reusable order details table
 */

import React from 'react';
import type { OrderTableColumn, OrderTableConfig } from '../types/components';
import type { OrderSummary } from '../utils/types';
import { isGlassValueArrival, orderHasImagesColumnData } from './orderTableHelpers';
import { isOrderGAP } from './gapDetection';

/**
 * Complete column definitions for order details table
 * All 40+ columns from existing implementations
 */
export const DEFAULT_ORDER_COLUMNS: OrderTableColumn[] = [
  // ========================================
  // BASIC INFORMATION (Always Visible)
  // ========================================
  { 
    key: 'product_type', 
    label: 'Product Type', 
    type: 'text', 
    group: 'basic',
    width: '120px',
    render: (value) => value ? value.charAt(0).toUpperCase() + value.slice(1) : '-'
  },
  { 
    key: 'glass_width', 
    label: 'Width (mm)', 
    type: 'number', 
    group: 'basic',
    align: 'right'
  },
  { 
    key: 'glass_height', 
    label: 'Height (mm)', 
    type: 'number', 
    group: 'basic',
    align: 'right'
  },
  { 
    key: 'diameter_mm', 
    label: 'Diameter (mm)', 
    type: 'number', 
    group: 'basic',
    align: 'right',
    condition: { hideIfEmpty: true }
  },
  { 
    key: 'quantity', 
    label: 'Qty', 
    type: 'number', 
    group: 'basic',
    width: '80px',
    align: 'center',
    condition: {
      styles: {
        backgroundColor: (value: any) => {
          const qty = typeof value === 'number' ? value : (value ? parseInt(value, 10) : 0);
          if (qty === 1) return 'bg-gray-100';
          if (qty === 2) return 'bg-teal-100';
          if (qty === 3) return 'bg-purple-100';
          if (qty === 4) return 'bg-pink-100';
          if (qty === 5) return 'bg-indigo-100';
          if (qty > 5) return 'bg-cyan-100';
          return '';
        },
        textColor: () => 'font-bold'
      }
    }
  },
  { 
    key: 'rack', 
    label: 'Rack', 
    type: 'component', 
    group: 'basic',
    width: '120px'
  },
  { 
    key: 'reference', 
    label: 'Reference', 
    type: 'text', 
    group: 'basic',
    condition: { hideIfEmpty: true },
    textDisplay: { expandable: true, collapsedLines: 3 },
  },
  { 
    key: 'notes', 
    label: 'Notes', 
    type: 'text', 
    group: 'basic',
    condition: { hideIfEmpty: true },
    textDisplay: { expandable: true, collapsedLines: 3 },
  },
  { 
    key: 'unit_price', 
    label: 'Unit Price', 
    type: 'currency', 
    group: 'basic',
    condition: { hideIfEmpty: true }
  },

  // ========================================
  // GLASS SPECIFICATIONS
  // ========================================
  {
    key: 'metadata.glass_side_one_value_id',
    label: 'Glass Side One', 
    type: 'component',
    group: 'glass_specs',
    condition: {
      styles: {
        backgroundColor: (value: any, order: OrderSummary, glassValuesLookup?: Map<string, { is_arrival: boolean; is_cutting: boolean }>) => {
          if (isGlassValueArrival(order, 'metadata.glass_side_one_value_id', value, glassValuesLookup)) {
            return 'bg-blue-100';
          }
          return '';
        }
      }
    },
    // Will be handled by GlassValueDisplay component in the table
  },
  {
    key: 'metadata.glass_side_two_value_id',
    label: 'Glass Side Two', 
    type: 'component',
    group: 'glass_specs',
    condition: { 
      hideIfEmpty: true,
      styles: {
        backgroundColor: (value: any, order: OrderSummary, glassValuesLookup?: Map<string, { is_arrival: boolean; is_cutting: boolean }>) => {
          if (isGlassValueArrival(order, 'metadata.glass_side_two_value_id', value, glassValuesLookup)) {
            return 'bg-blue-100';
          }
          return '';
        }
      }
    },
    // Will be handled by GlassValueDisplay component in the table
  },
  {
    // Triple orders use middle_glass_panel_value_id; double/single may use glass_side_three_value_id
    key: 'metadata.middle_glass_panel_value_id',
    label: 'Glass Side Three',
    type: 'component',
    group: 'glass_specs',
    condition: {
      hideIfEmpty: true,
      styles: {
        backgroundColor: (value: any, order: OrderSummary, glassValuesLookup?: Map<string, { is_arrival: boolean; is_cutting: boolean }>) => {
          if (isGlassValueArrival(order, 'metadata.middle_glass_panel_value_id', value, glassValuesLookup)) {
            return 'bg-blue-100';
          }
          return '';
        }
      }
    },
    // Will be handled by GlassValueDisplay component in the table
  },

  // ========================================
  // SINGLE GLAZED OPTIONS
  // ========================================
  { 
    key: 'polished_edges', 
    label: 'Polished', 
    type: 'boolean', 
    group: 'single_glazed',
    condition: { hideIfEmpty: true },
    render: (value) => value ? React.createElement('span', { className: 'font-bold bg-cyan-100 px-2 py-1 rounded' }, 'POL') : '-'
  },
  { 
    key: 'dubbed_corners', 
    label: 'Dubbed', 
    type: 'boolean', 
    group: 'single_glazed',
    condition: { hideIfEmpty: true },
    render: (value) => value ? React.createElement('span', { className: 'font-bold bg-purple-100 px-2 py-1 rounded' }, 'DUB') : '-'
  },
  
  // ========================================
  // DRILLED HOLES
  // ========================================
  { 
    key: 'hole_size_mm', 
    label: 'Hole Size (mm)', 
    type: 'number', 
    group: 'drilled_holes',
    condition: { hideIfEmpty: true },
    align: 'right'
  },
  { 
    key: 'hole_count', 
    label: 'Number of holes', 
    type: 'number', 
    group: 'drilled_holes',
    condition: { hideIfEmpty: true },
    align: 'center'
  },
  { 
    key: 'hole_locations', 
    label: 'Hole Locations', 
    type: 'text', 
    group: 'drilled_holes',
    condition: { hideIfEmpty: true }
  },

  // ========================================
  // CUTOUTS
  // ========================================
  { 
    key: 'cutout_shape', 
    label: 'Cut-out shape', 
    type: 'text', 
    group: 'cutouts',
    condition: { hideIfEmpty: true },
    render: (value) => value ? value.charAt(0).toUpperCase() + value.slice(1) : '-'
  },
  { 
    key: 'cutout_count', 
    label: 'Number of Cutouts', 
    type: 'number', 
    group: 'cutouts',
    condition: { hideIfEmpty: true },
    align: 'center'
  },
  { 
    key: 'cutout_diameter_mm', 
    label: 'Cut-out Diameter (mm)', 
    type: 'number', 
    group: 'cutouts',
    condition: { hideIfEmpty: true },
    align: 'right'
  },
  { 
    key: 'cutout_position', 
    label: 'Cut-out Position', 
    type: 'text', 
    group: 'cutouts',
    condition: { hideIfEmpty: true }
  },
  { 
    key: 'cutout_width_mm', 
    label: 'Cut-out Width (mm)', 
    type: 'number', 
    group: 'cutouts',
    condition: { hideIfEmpty: true },
    align: 'right'
  },
  { 
    key: 'cutout_height_mm', 
    label: 'Cut-out Height (mm)', 
    type: 'number', 
    group: 'cutouts',
    condition: { hideIfEmpty: true },
    align: 'right'
  },

  // ========================================
  // ROUNDED CORNERS
  // ========================================
  { 
    key: 'rounded_corner_radius_mm', 
    label: 'Corner radius (mm)', 
    type: 'number', 
    group: 'rounded_corners',
    condition: { hideIfEmpty: true },
    align: 'right'
  },
  { 
    key: 'rounded_corner_count', 
    label: 'Total radius corners', 
    type: 'number', 
    group: 'rounded_corners',
    condition: { hideIfEmpty: true },
    align: 'center'
  },
  { 
    key: 'rounded_corner_locations', 
    label: 'Corner Locations', 
    type: 'text', 
    group: 'rounded_corners',
    condition: { hideIfEmpty: true }
  },

  // ========================================
  // DOUBLE GLAZED OPTIONS
  // ========================================
  { 
    key: 'spacer_bar_type', 
    label: 'Spacer Type', 
    type: 'text', 
    group: 'double_glazed',
    condition: { hideIfEmpty: true },
    render: (value) => {
      if (!value) return '-';
      return value === 'aluminum' ? 'Aluminum' : 'Warm Edge';
    }
  },
  { 
    key: 'spacer_bar_color', 
    label: 'Spacer Colour', 
    type: 'text', 
    group: 'double_glazed',
    condition: { hideIfEmpty: true },
    render: (value) => value ? value.charAt(0).toUpperCase() + value.slice(1) : '-'
  },
  { 
    key: 'spacer_bar_thickness_mm', 
    label: 'Spacer Thickness (mm)', 
    type: 'number', 
    group: 'double_glazed',
    condition: { hideIfEmpty: true },
    align: 'right'
  },
  { 
    key: 'argon_filled', 
    label: 'Argon Filled', 
    type: 'boolean', 
    group: 'double_glazed',
    condition: { hideIfEmpty: true },
    render: (value) => value ? React.createElement('span', { className: 'font-bold bg-green-100 px-2 py-1 rounded' }, 'ARG') : '-'
  },

  // ========================================
  // DECORATIVE LEADS
  // ========================================
  { 
    key: 'decorative_leaded_pattern', 
    label: 'Leaded Pattern', 
    type: 'text', 
    group: 'decorative_leads',
    condition: { hideIfEmpty: true },
    render: (value) => value ? value.charAt(0).toUpperCase() + value.slice(1) : '-'
  },
  { 
    key: 'decorative_lead_color', 
    label: 'Lead Colour', 
    type: 'text', 
    group: 'decorative_leads',
    condition: { hideIfEmpty: true },
    render: (value) => value ? value.charAt(0).toUpperCase() + value.slice(1) : '-'
  },
  { 
    key: 'decorative_lead_thickness_mm', 
    label: 'Lead Thickness (mm)', 
    type: 'number', 
    group: 'decorative_leads',
    condition: { hideIfEmpty: true },
    align: 'right'
  },
  { 
    key: 'decorative_lead_soldering_required', 
    label: 'Soldering', 
    type: 'boolean', 
    group: 'decorative_leads',
    condition: { hideIfEmpty: true },
    render: (value) => value ? React.createElement('span', { className: 'font-bold bg-amber-100 px-2 py-1 rounded' }, 'SOL') : '-'
  },

  // ========================================
  // DUPLEX BAR
  // ========================================
  { 
    key: 'duplex_bar_type', 
    label: 'Duplex Type', 
    type: 'text', 
    group: 'duplex_bar',
    condition: { hideIfEmpty: true },
    render: (value) => value ? value.charAt(0).toUpperCase() + value.slice(1) : '-'
  },
  { 
    key: 'duplex_bar_thickness', 
    label: 'Duplex Thickness', 
    type: 'text', 
    group: 'duplex_bar',
    condition: { hideIfEmpty: true }
  },

  // ========================================
  // GEORGIAN BAR
  // ========================================
  { 
    key: 'georgian_bar_type', 
    label: 'Georgian Type', 
    type: 'text', 
    group: 'georgian_bar',
    condition: { hideIfEmpty: true },
    render: (value) => value ? value.charAt(0).toUpperCase() + value.slice(1) : '-'
  },
  { 
    key: 'georgian_bar_thickness', 
    label: 'Georgian Thickness', 
    type: 'text', 
    group: 'georgian_bar',
    condition: { hideIfEmpty: true }
  },

  // ========================================
  // TRIPLE GLAZED OPTIONS
  // ========================================
  { 
    key: 'spacer_bar2_type', 
    label: 'Spacer2 Type', 
    type: 'text', 
    group: 'triple_glazed',
    condition: { hideIfEmpty: true },
    render: (value) => {
      if (!value) return '-';
      return value === 'aluminum' ? 'Aluminum' : 'Warm Edge';
    }
  },
  { 
    key: 'spacer_bar2_color', 
    label: 'Spacer2 Colour', 
    type: 'text', 
    group: 'triple_glazed',
    condition: { hideIfEmpty: true },
    render: (value) => value ? value.charAt(0).toUpperCase() + value.slice(1) : '-'
  },
  { 
    key: 'spacer_bar2_thickness_mm', 
    label: 'Spacer2 Thickness (mm)', 
    type: 'number', 
    group: 'triple_glazed',
    condition: { hideIfEmpty: true },
    align: 'right'
  },

  // ========================================
  // MIRROR OPTIONS
  // ========================================
  { 
    key: 'aluminium_backing_foil', 
    label: 'Aluminium Foil', 
    type: 'boolean', 
    group: 'mirror',
    condition: { hideIfEmpty: true },
    render: (value) => value ? React.createElement('span', { className: 'font-bold bg-slate-200 px-2 py-1 rounded' }, 'ALF') : '-'
  },
  { 
    key: 'safety_backing', 
    label: 'Safety Backing', 
    type: 'boolean', 
    group: 'mirror',
    condition: { hideIfEmpty: true },
    render: (value) => value ? React.createElement('span', { className: 'font-bold bg-rose-100 px-2 py-1 rounded' }, 'SB') : '-'
  },

  // ========================================
  // METADATA & TIMESTAMPS
  // ========================================
  // { 
  //   key: 'created_at', 
  //   label: 'Created', 
  //   type: 'date', 
  //   group: 'metadata',
  //   render: (value) => new Date(value).toLocaleString()
  // },

  // ========================================
  // ORDER IMAGES (Handled by OrderImagesCells component)
  // Shown only when at least one row has S3 images or Woo drawing URLs (see orderHasImagesColumnData).
  // ========================================
  { 
    key: 'images_available', 
    label: 'Image', 
    type: 'component', 
    group: 'images',
    width: '80px',
    align: 'center',
    condition: {
      hasDataCheck: orderHasImagesColumnData,
    },
  },
  { 
    key: 'image_types', 
    label: 'Image Type', 
    type: 'component', 
    group: 'images',
    width: '120px',
    condition: {
      hasDataCheck: orderHasImagesColumnData,
    },
  },
  { 
    key: 'image_actions', 
    label: 'View', 
    type: 'component', 
    group: 'images',
    width: '80px',
    align: 'center',
    condition: {
      hasDataCheck: orderHasImagesColumnData,
    },
  }
];

/**
 * Default styling rules for conditional formatting
 */
export const DEFAULT_STYLING_RULES = {
  // Row-level styling based on order properties
  // Note: This function doesn't have access to glass values lookup.
  // For better GAP detection (especially Condition Two), use OrderDetailsTable
  // which automatically fetches glass values and passes them to isOrderGAP.
  rowClass: (order: OrderSummary): string => {
    // Apply light yellow background for GAP orders
    // This will work for Condition One and Three, and Condition Two if *_is_arrival fields are available
    if (isOrderGAP(order)) {
      return 'bg-yellow-100';
    }
    return '';
  },
  
  // Column-specific styling rules
  columnStyles: {
    quantity: {
      backgroundColor: (value: number): string => value > 5 ? 'bg-blue-50' : '',
      textColor: (value: number): string => value > 10 ? 'text-blue-700 font-semibold' : ''
    },
    product_type: {
      textColor: (value: string): string => {
        switch (value) {
          case 'triple': return 'text-green-700 font-medium';
          case 'mirror': return 'text-purple-700 font-medium';  
          case 'double': return 'text-blue-700 font-medium';
          default: return '';
        }
      }
    },
    unit_price: {
      textColor: (value: string | number): string => {
        const price = typeof value === 'string' ? parseFloat(value) : value;
        return price > 100 ? 'text-green-700 font-semibold' : '';
      }
    }
  }
};

/**
 * Column groups for logical organization
 */
export const COLUMN_GROUPS = {
  basic: { label: 'Basic Information', priority: 1, defaultExpanded: true },
  glass_specs: { label: 'Glass Specifications', priority: 2, defaultExpanded: true },
  single_glazed: { label: 'Single Glazed Options', priority: 3, defaultExpanded: false },
  drilled_holes: { label: 'Drilled Holes', priority: 4, defaultExpanded: false },
  cutouts: { label: 'Cutouts', priority: 5, defaultExpanded: false },
  rounded_corners: { label: 'Rounded Corners', priority: 6, defaultExpanded: false },
  double_glazed: { label: 'Double Glazed Options', priority: 7, defaultExpanded: false },
  decorative_leads: { label: 'Decorative Leads', priority: 8, defaultExpanded: false },
  duplex_bar: { label: 'Duplex Bar', priority: 9, defaultExpanded: false },
  georgian_bar: { label: 'Georgian Bar', priority: 10, defaultExpanded: false },
  triple_glazed: { label: 'Triple Glazed Options', priority: 11, defaultExpanded: false },
  mirror: { label: 'Mirror Options', priority: 12, defaultExpanded: false },
  images: { label: 'Order Images', priority: 13, defaultExpanded: false },
  metadata: { label: 'Metadata', priority: 14, defaultExpanded: false }
};

/**
 * Default configuration for the order details table
 */
export const DEFAULT_ORDER_TABLE_CONFIG: OrderTableConfig = {
  columns: DEFAULT_ORDER_COLUMNS,
  styling: {
    tableClass: 'min-w-full border text-sm',
    headerClass: 'bg-gray-50',
    bodyClass: 'bg-white divide-y divide-gray-200',
    rowClass: DEFAULT_STYLING_RULES.rowClass
  },
  features: {
    autoHideEmptyColumns: true,
    showGroupHeaders: false,
    responsive: true
  }
};
